<template>
  <default-modal
    v-model="modalOpened"
    name="qr-modal"
    width="sm"
    :title="$t('otp_enable_title')"
    :autofocus="$refs.verificationCode"
    @hide="handleClose"
  >
    <template slot="content">
      <q-stepper ref="tfaStepper">
        <!-- Step 1: Setup 2FA -->
        <q-step default title="setup">
          <p>{{ $t('otp_enable_body') }}</p>

          <div v-html="qrImage" class="qr-modal__image row justify-center"></div>

          <i18n tag="p" class="text-faded mt-1" path="otp_enable_hint">
            <a place="manualSetup" class="primary-link" href="#" @click.prevent="handleManualSetup">
              {{ $t('otp_enable_manual') }}
            </a>
          </i18n>

          <!-- Manual 2FA setup -->
          <div v-if="manualSetup" class="qr-modal__code_area">
            <table>
              <tr>
                <td class="text-bold">{{ $t('algorithm') }}:</td>
                <td>SHA1</td>
              </tr>
              <tr>
                <td class="text-bold">{{ $t('digits') }}:</td>
                <td>6</td>
              </tr>
              <tr>
                <td class="text-bold">{{ $t('mode') }}:</td>
                <td>TOTP</td>
              </tr>
              <tr>
                <td class="text-bold">{{ $t('interval') }}:</td>
                <td>30 {{ $tc('second', 2) }}</td>
              </tr>
              <tr>
                <td class="text-bold">{{ $t('secret') }}:</td>
                <td>{{ setupSecret }}</td>
              </tr>
            </table>
          </div>

          <div class="q-mt-lg row justify-between items-end">
            <q-input
              ref="verificationCode"
              v-model="verificationCode"
              placeholder="OTP Code"
              class="qr-modal__input inline"
            />

            <q-btn
              class="inline on-right"
              color="positive"
              @click="handleVerify"
            >
              {{ $t('confirm') }}
            </q-btn>
          </div>
        </q-step>

        <!-- Step 2: 2FA backup codes -->
        <q-step title="backup">
          <p v-html="$t('backup_codes_body')"></p>

          <backup-codes-area :backup-codes="backupCodes" class="q-mt-lg"/>

          <q-btn
            class="full-width q-mt-lg"
            color="positive"
            @click="handleClose"
          >
            {{ $t('confirm') }}
          </q-btn>
        </q-step>

        <q-inner-loading :visible="loading"/>
      </q-stepper>
    </template>
  </default-modal>
</template>

<script>
  import BackupCodesArea from './Components/BackupCodesArea.vue'
  import DefaultModal from 'components/Modal/DefaultModal.vue'
  import { UserClient } from 'src/api/Users'
  import { auth } from 'src/config'
  import { createErrorFromResponse } from 'src/utils/error'
  import { mapActions } from 'vuex'
  import qr from 'qr-image'

  export default {
    data () {
      return {
        modalOpened: false,
        setupUrl: null,
        verificationCode: null,
        qrImage: null,
        backupCodes: [],
        manualSetup: false,
        loading: false
      }
    },

    computed: {
      setupSecret () {
        return (new URL(this.setupUrl)).searchParams.get('secret')
      }
    },

    methods: {
      ...mapActions(['logout', 'set2FAState']),

      open () {
        // Reset data
        this.verificationCode = null
        this.manualSetup = false

        new UserClient()
          .enable2FA()
          .then(response => {
            this.setupUrl = response.data.data.qr_code
            this.qrImage = qr.imageSync(this.setupUrl, { type: 'svg' })
            this.modalOpened = true
          })
          .catch(createErrorFromResponse)
      },

      handleManualSetup () {
        this.manualSetup = true
      },

      handleVerify () {
        this.loading = true

        new UserClient()
          .verify2FA(this.verificationCode)
          .then(response => {
            this.set2FAState(true)

            this.qrImage = null
            this.verificationCode = null
            this.backupCodes = response.data.backup_codes

            this.$refs.tfaStepper.next()
          })
          .catch(createErrorFromResponse)
          .then(() => {
            this.loading = false
          })
      },

      handleClose () {
        if (!this.backupCodes.length) {
          // Modal was closed before setup has been finished
          return
        }

        this.$q.notify({ message: this.$t('tfa_enabled'), type: 'positive' })

        this.logout()
        this.$router.push({ name: 'login' })
      }
    },

    watch: {
      verificationCode (code) {
        if (!code || code.length !== auth.otp_length) return

        this.handleVerify()
      }
    },

    components: {
      BackupCodesArea,
      DefaultModal
    }
  }
</script>

<style lang="stylus">
  @import '~variables'

  .qr-modal
    &__image
      > svg
        width: 15rem

    &__input
      margin: 0
      flex-grow: 0.9

    & .q-stepper
      box-shadow: none
      > .q-stepper-header
        display: none
      & .q-stepper-step-inner
        padding: 10px 0 24px 0 !important
        color: rgba(0, 0, 0, 0.5) !important

    > .modal-body
      padding: 0 24px

    &__code_area
      padding: 1rem
      background-color: $body-background
      border: 1px solid $border-color
      border-radius: $generic-border-radius
</style>
